import { DEMO } from "./demo/flag";
import { MyPlayNotice } from "./ui/MyPlayNotice";
import { useMyPlaysSync } from "./myPlays";
import { Planner } from "./pages/admin/Planner";
import { SoundLab } from "./pages/admin/SoundLab";
import { CookieBanner } from "./ui/CookieBanner";
import { PreviewBar } from "./river/PreviewBar";
import { stopPreview } from "./river/context";
import { FamePage } from "./pages/Fame";
import { Mixtapes } from "./pages/Mixtapes";
import { MyTapes, TapePage } from "./pages/MyTapes";
import { stopTape } from "./tapes/tapePlayer";
import { TapeDialogHost } from "./tapes/AddToTape";
import { MixtapeTape } from "./pages/MixtapeTape";
import { stopMixtape } from "./countdown/mixtape";
import { Join } from "./pages/Join";
import { TourOverlay } from "./tour/TourOverlay";
import { TourWelcome } from "./tour/TourLaunch";
import { useEffect, lazy, Suspense } from "react";
import { Header } from "./ui/Header";
import { EigenbloomBackdrop } from "./skins/EigenbloomBackdrop";
import { exitImmersive, ImmersiveExit } from "./skins/immersive";
import { Footer } from "./ui/Footer";
import { useSettings } from "./ui/settings";
import { useMedia } from "./ui/useMedia";
import { match, usePath } from "./lib/router";
import { Calendar } from "./pages/Calendar";
import { Day } from "./pages/Day";
import { LoopPage } from "./pages/LoopPage";
import { ProfilePage } from "./pages/Profile";
import { Admin } from "./pages/Admin";
import { LEGAL_PAGES } from "./pages/legal";
import { Lab } from "./pages/Lab";
import { LabZoo } from "./pages/LabZoo";
import { River } from "./pages/River";
import { Write } from "./pages/Write";
import { NotFound } from "./pages/NotFound";
import { SignIn } from "./pages/SignIn";
import { Verify } from "./pages/Verify";
import { Account } from "./pages/Account";
import { DevMailbox } from "./pages/DevMailbox";
import { loadMe, saveAccountSettings, useSession } from "./session";

// Demo-only UI, loaded lazily behind the compile-time flag so the normal build drops it (and the
// Wizard it pulls in).
const DemoPanel = __EB_DEMO__
  ? lazy(() => import("./demo/DemoPanel").then((m) => ({ default: m.DemoPanel })))
  : () => null;
const DemoSignIn = __EB_DEMO__
  ? lazy(() => import("./demo/DemoSignIn").then((m) => ({ default: m.DemoSignIn })))
  : () => null;

function Routes() {
  const path = usePath();
  if (path === "/") return <River />;
  if (path === "/lab") return <Lab />;
  if (path === "/lab/zoo") return <LabZoo />;
  if (path === "/write") return <Write />;
  if (path === "/signin" || path === "/join")
    return DEMO ? (
      <Suspense fallback={null}>
        <DemoSignIn />
      </Suspense>
    ) : path === "/signin" ? (
      <SignIn />
    ) : (
      <Join />
    );
  if (path === "/fame") return <FamePage />;
  if (path === "/mixtapes") return <Mixtapes />;
  if (path === "/tapes") return <MyTapes />;
  if (path === "/countdown") return <Mixtapes />;
  if (path === "/auth/verify") return <Verify />;
  if (path === "/account") return <Account />;
  if (path === "/dev/mailbox") return <DevMailbox />;
  if (path === "/calendar") return <Calendar />;
  if (path === "/admin") return <Admin />;
  if (path === "/admin/planner") return <Planner />;
  if (path === "/admin/lab") return <SoundLab />;
  let m = match("/day/:date", path);
  if (m) return <Day date={m.date} />;
  m = match("/loop/:id", path);
  if (m) return <LoopPage id={m.id} />;
  m = match("/tapes/:id", path);
  if (m) return <TapePage key={m.id} id={m.id} />;
  m = match("/mixtapes/:day", path);
  if (m) return <MixtapeTape key={m.day} day={Number(m.day)} />;
  m = match("/legal/:slug", path);
  if (m) {
    // The German privacy page was merged into the Privacy Policy; old links still land there.
    const slug = m.slug === "datenschutz" ? "privacy" : m.slug;
    const page = LEGAL_PAGES.find((p) => p.slug === slug);
    if (page) return <page.Component />;
  }
  m = match("/u/:nickname", path);
  if (m) return <ProfilePage nickname={m.nickname} />;
  return <NotFound />;
}

export function App() {
  const { skin } = useSettings();
  const calm = useMedia("(prefers-reduced-motion: reduce)");
  const path = usePath();
  // The signed-in contributor's own plays (the notice under the header, the River highlight).
  useMyPlaysSync();
  // Full screen belongs to the Eigenbloom tunnel: another look or page leaves it.
  useEffect(() => exitImmersive(), [skin, path]);
  // Leaving a page ends any song preview or countdown it started.
  useEffect(() => {
    void stopPreview();
    if (!path.startsWith("/mixtapes/")) stopMixtape();
    if (!path.startsWith("/tapes/")) stopTape();
  }, [path]);
  useEffect(() => {
    void loadMe().catch(() => undefined);
  }, []);
  // Skin choice follows the account once signed in.
  useEffect(() => {
    const u = useSession.getState().user;
    if (u && u.skin !== skin) void saveAccountSettings({ skin }).catch(() => undefined);
  }, [skin]);
  useEffect(() => {
    document.documentElement.dataset.skin = skin;
    document.documentElement.dataset.motion = calm ? "calm" : "full";
  }, [skin, calm]);
  return (
    <>
      {/* Eigenbloom: the tunnel fills the window behind the interface (not in the editor, which
          plays its own music). */}
      {skin === "eigenbloom" && !path.startsWith("/write") && <EigenbloomBackdrop />}
      {skin === "eigenbloom" && <ImmersiveExit />}
      <Header />
      <MyPlayNotice />
      <Routes />
      <Footer />
      <CookieBanner />
      <PreviewBar />
      <TapeDialogHost />
      <TourWelcome />
      <TourOverlay />
      {DEMO && (
        <Suspense fallback={null}>
          <DemoPanel />
        </Suspense>
      )}
    </>
  );
}
